import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { BaseComponent } from '../../base.component';
import { Account, SiteConfig } from '../../shared/models';
import { Features } from '../../shared/models/features.enum';
import { SessionService } from '../../shared/session.service';
import { englishLanguages } from '../../shared/language-selector/languages';
import { takeUntil } from 'rxjs/operators';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.css']
})
export class NavigationComponent extends BaseComponent implements OnInit {
  @Input() hasExternalToolbar: boolean;
  @Output() navigated: EventEmitter<any> = new EventEmitter();

  Features = Features;
  today = new Date();
  account: Account;
  sessionIsReady: boolean;
  siteConfig: SiteConfig;
  sessionIsAuthorized: boolean;
  isEnglishLanguage: boolean;
  isAccessibilityWidgetAppended: boolean;
  hideSeoListings: boolean = true;
  hideBusinessCitations: boolean = true;
  isLandingPage: boolean;

  constructor(
    private router: Router,
    private elementRef: ElementRef,
    public sessionService: SessionService) {
    super();
  }

  featureVisibility: Record<Features, { hasFeature: boolean, isActive: boolean }> = {
    [Features.SEO]: { hasFeature: false, isActive: false },
    [Features.BusinessCitations]: { hasFeature: false, isActive: false },
    [Features.BusinessProfileManagement]: { hasFeature: false, isActive: false },
    [Features.StandaloneBacklink]: { hasFeature: false, isActive: false },
    [Features.PageOptimization]: { hasFeature: false, isActive: false },
    [Features.StarterSEO]: { hasFeature: false, isActive: false }
  };

  ngOnInit() {
    combineLatest([
      this.sessionService.account,
      this.sessionService.siteConfig,
      this.sessionService.isLandingPage,
      this.sessionService.isAuthorized(),
      this.sessionService.isReady()
    ])
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(([account, siteConfig, isLandingPage, sessionIsAuthorized, sessionIsReady]) => {
      this.account = account;
      this.setFeatureVisbility(this.account);
      this.siteConfig = siteConfig;
      if (this.siteConfig && this.siteConfig.hiddenContent) {
        const seoCitationsKey = 'Seo-Business-Citations';
        const bpmKey = 'BusinessProfileManagement';
        this.hideSeoListings = this.siteConfig.hiddenContent.some(x => x === seoCitationsKey);
        this.hideBusinessCitations = this.siteConfig.hiddenContent.some(x => x === bpmKey);
        this.configureAccessibilityVisibility();
      }
      this.isLandingPage = isLandingPage;
      this.sessionIsAuthorized = sessionIsAuthorized;
      this.sessionIsReady = sessionIsReady;
    });
  }

  onNavigation() {
    this.navigated.emit(null);
  }

  logoff() {
    this.router.navigateByUrl('log-out');
  }

  toggleFeature(feature: Features) {
    this.featureVisibility[feature].isActive = !this.featureVisibility[feature].isActive;
  }

  private setFeatureVisbility(account: Account) {
    if (!account || !account.hasFeature) {
      return;
    }

    const hasSeo = account.hasFeature(Features.SEO) || account.hasFeature(Features.StarterSEO);

    this.featureVisibility[Features.SEO].hasFeature = hasSeo;
    this.featureVisibility[Features.BusinessCitations].hasFeature = account.hasFeature(Features.BusinessCitations);
    this.featureVisibility[Features.BusinessProfileManagement].hasFeature = account.hasFeature(Features.BusinessProfileManagement);
    this.featureVisibility[Features.StandaloneBacklink].hasFeature = account.hasFeature(Features.StandaloneBacklink) && !hasSeo;
    this.featureVisibility[Features.PageOptimization].hasFeature = account.hasFeature(Features.PageOptimization) && !hasSeo;
    for (let feature of Object.values(this.featureVisibility)) {
      feature.isActive = feature.hasFeature;
    }
  }

  private configureAccessibilityVisibility() {
    if (this.isAccessibilityWidgetAppended) {
      return;
    }

    this.isEnglishLanguage = englishLanguages.indexOf(this.sessionService.locale) !== -1;

    const accessibilityHideKey = 'SIDEBAR-ACCESSIBILITY';
    const accessibilityIsHidden = this.siteConfig.hiddenContent.some(x => x.toUpperCase() === accessibilityHideKey);

    if (this.isEnglishLanguage && !accessibilityIsHidden && !this.isAccessibilityWidgetAppended) {
      this.isAccessibilityWidgetAppended = true;
      this.appendAccessibilityWidget();
    }
  }

  private appendAccessibilityWidget() {
    const widgetScript = document.createElement('script');
    widgetScript.type = 'text/javascript';
    widgetScript.src = 'assets/accessibility.js';
    this.elementRef.nativeElement.appendChild(widgetScript);
  }
}
